// The map author's marks on the WebGL2 engine (arcade.md §4.9, R303, R500).
//
// A marker says "this tile" and does nothing else. It carries no gameplay,
// it is the same for every viewer, and it never changes during a match: the
// whole list arrives one time in `Hello.arcade.markers` and no state frame
// touches it. So this is the simplest layer in `fast/layers/`: it packs its
// instances once in `set()` and has no clock, no cull and no per-frame work
// at all — every later frame re-flushes what the one pack wrote.
//
// The geometry is `render/marks.ts`, baked into the `marker-arrow` and
// `marker-circle` atlas cells (`gfx/atlas.ts`), so the picture the designer
// draws and the picture a player sees come from one description. The cells
// are baked white and each instance carries the author's own colour as its
// tint, which is why two shapes serve every colour at no extra upload.
//
// There are at most `arcade.MaxMarkers` markers on a map, so the batch is
// sized for that and never grows.
//
// The layer also carries the map spot of a drill step (arcade.md §17.8,
// R1573): the step's cell and the arrowheads that close on it, the same
// arrowheads round the own hull of the step's class, and the flash of the
// step-done moment. They are `drillspot.ts`, built on the
// first spot, so a match that is no drill asks the engine for no batch and
// keeps the static layer it always was.
//
// The layer also carries the commission marks of v2 phase 5
// (v2-the-deep.md §14.6, R2645, ui-map-interface.md §6.7): one pennant at
// the target cell of each commission of `Empire.commissions`, in the own
// faction ink, a taken offer solid and an open one at `openAlpha`. It is a
// glyph of its own, so every look of `mapMarker` keeps it, and it is no
// `MarkerKind`. The layer reads no flag: it draws what the wire sends, and
// the wire sends no commission while the flag is off. The batch is built on
// the first commission, so a world with none asks the engine for nothing.

// MAX_MARKERS is the map file's own ceiling, `arcade.MaxMarkers`: both sides
// read it from data/contracts/arcade-schema.toml.
import { MARKER_CIRCLE, MAX_MARKERS } from '../../gen/balance';
import { COMMISSION_MARK, MARKER_NEUTRAL } from '../../gen/presentation';
import type { ArcadeMarker, Commission } from '../../gen/proto/aquaphobia';
import { COMMISSION_MARK_KEY } from '../../render/commission-mark';
import { cellCenter, factionColor } from '../../world';
import type { AtlasEntry } from '../gfx/atlas';
import type { DrawCtx, FrameInfo, Layer } from '../layer';
import type { BatchSource, QuadSink } from './batches';
import { DrillSpot, type DrillSpotCell, type HullAt } from './drillspot';

/** The colour of a marker: the neutral white for 0, else the faction's own. */
export function markerColor(color: number): number {
  return color === 0 ? MARKER_NEUTRAL : factionColor(color);
}

export class MarkerLayer implements Layer {
  readonly name = 'markers';

  private readonly quads: QuadSink;
  private readonly arrow: AtlasEntry;
  private readonly circle: AtlasEntry;
  private shown = true;
  /** The list of the last `set`, which a repack of the atlas packs again. */
  private last: readonly ArcadeMarker[] = [];
  /** The drill's marks, or null until the first spot or flash (R1573). */
  private drill: DrillSpot | null = null;
  /** The commission marks, or null until the first commission (R2645). */
  private comm: QuadSink | null = null;
  /** The list of the last `setCommissions` and its faction, which a
   *  repack packs again. */
  private commLast: readonly Commission[] = [];
  private commFaction = 0;

  /** `hullAt` answers where an own hull stands, for the hull mark of a
   *  drill step (arcade.md §17.8); a page with no fleets answers null. */
  constructor(private readonly src: BatchSource, private readonly hullAt: HullAt = () => null) {
    this.quads = src.newQuads(MAX_MARKERS);
    this.arrow = src.sprite('marker-arrow');
    this.circle = src.sprite('marker-circle');
  }

  /** How many marks the layer holds. The tests read it. */
  get count(): number { return this.quads.count; }

  /** Takes the whole list a hello named, and packs it once. */
  set(markers: readonly ArcadeMarker[]): void {
    this.last = markers;
    const q = this.quads;
    q.begin();
    const n = Math.min(markers.length, MAX_MARKERS);
    for (let i = 0; i < n; i++) {
      const m = markers[i];
      const [x, y] = cellCenter(m.cell);
      const circle = m.shape === MARKER_CIRCLE;
      // Heading is degrees clockwise from north, and a mark points along its
      // own +x axis, so north is a quarter turn back. A circle points
      // nowhere and the validator keeps its heading at 0.
      const rot = circle ? 0 : ((m.heading - 90) * Math.PI) / 180;
      q.sprite(x, y, rot, circle ? this.circle : this.arrow, markerColor(m.color), 1);
    }
  }

  /** Drops every mark. A hello with no arcade half calls it. */
  clear(): void { this.last = []; this.quads.begin(); this.drill?.set(null); this.drill?.setHull(0); }

  /** Packs the last lists again, because an atlas grow moved the cells. */
  repack(): void {
    this.set(this.last);
    if (this.comm) this.setCommissions(this.commLast, this.commFaction);
  }

  /** How many commission marks the layer holds. The tests read it. */
  get commissionCount(): number { return this.comm?.count ?? 0; }

  /**
   * The commission marks (R2645): one pennant at the target cell of each
   * commission the Empire frame lists, in the ink of `faction`. A taken
   * offer stands solid, an open one at `openAlpha`. The scene calls it when
   * the frame hands over a new list.
   */
  setCommissions(list: readonly Commission[], faction: number): void {
    this.commLast = list;
    this.commFaction = faction;
    if (list.length === 0 && this.comm === null) return;
    this.comm ??= this.src.newQuads(COMMISSION_MARK.maxMarks);
    const q = this.comm;
    q.begin();
    const entry = this.src.sprite(COMMISSION_MARK_KEY);
    const ink = factionColor(faction);
    const n = Math.min(list.length, COMMISSION_MARK.maxMarks);
    for (let i = 0; i < n; i++) {
      const c = list[i];
      const [x, y] = cellCenter(c.cell);
      q.sprite(x, y, 0, entry, ink, c.fleetId !== 0 ? 1 : COMMISSION_MARK.openAlpha);
    }
  }

  /** The map spot of the drill step that stands, or null (R1573). */
  setDrillSpot(spot: DrillSpotCell | null): void {
    if (spot === null && this.drill === null) return;
    this.drillMarks().set(spot);
  }

  /** The own hull a drill step marks, or 0 (arcade.md §17.8). */
  setDrillHull(id: number): void {
    if (id === 0 && this.drill === null) return;
    this.drillMarks().setHull(id);
  }

  /** The flash of a done drill step where its act landed, in world px. */
  drillFlash(x: number, y: number): void { this.drillMarks().flash(x, y); }

  /** The drill's marks, built on first use. The tests read them. */
  drillMarks(): DrillSpot {
    this.drill ??= new DrillSpot(this.src, undefined, this.hullAt);
    return this.drill;
  }

  /** The author's marks are static for the life of the hello; only the
   *  drill's spot and flash move. */
  update(frame: FrameInfo): void {
    if (this.drill?.active) this.drill.update(frame);
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
    this.comm?.flush(ctx.cam);
    this.drill?.draw(ctx);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}
